'use client';
import {
  AgreementPlaceholderClass,
  parseRichText,
  splitAgreementPlaceholders,
} from 'bff';
import { useTranslations } from 'i11n';
import { Fragment, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, cn } from 'ui';

const PLACEHOLDER_STYLE: Record<AgreementPlaceholderClass, string> = {
  pending: 'border-stone-400 bg-stone-100 text-stone-700',
  absent: 'border-red-400 bg-red-50 text-red-700',
  unknown_key: 'border-red-400 bg-red-50 text-red-700',
};

/** El mismo formato en línea que imprime la conformidad: `*negrita*`, `_cursiva_`, `__subrayado__`. */
const FormattedText = ({ text }: { text: string }) => (
  <>
    {parseRichText(text).map((span, index) => (
      <span
        key={index}
        className={cn(
          span.bold && 'font-semibold',
          span.italic && 'italic',
          span.underline && 'underline',
        )}
      >
        {span.text}
      </span>
    ))}
  </>
);

/**
 * La carta del ofrecimiento tal como la lee el reclamante: su formato en línea
 * interpretado y, en una vista previa, los marcadores `⟦clase:clave⟧` dibujados
 * como etiquetas —gris lo que completa el reclamante, rojo lo que falta—.
 */
export const OfferMessageText = ({ text }: { text: string }) => {
  const t = useTranslations();
  const segments = useMemo(() => splitAgreementPlaceholders(text), [text]);

  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'text' ? (
          <Fragment key={index}>
            <FormattedText text={segment.text} />
          </Fragment>
        ) : (
          <span
            key={index}
            title={t(`offer.agreement.gapClasses.${segment.class}`)}
            className={cn(
              'mx-0.5 inline-flex items-center rounded border border-dashed px-1.5 py-0.5 align-baseline text-xs font-medium',
              PLACEHOLDER_STYLE[segment.class],
            )}
          >
            {t(`offer.agreement.placeholder.${segment.class}`, {
              label:
                t(`offer.agreement.variables.${segment.key}`) || segment.key,
              key: segment.key,
            })}
          </span>
        ),
      )}
    </>
  );
};

/** La carta en la tarjeta con la que se la muestra en el wizard y en Backoffice. */
export const OfferMessageCard = ({
  text,
  title,
  className,
}: {
  text: string;
  title?: string;
  className?: string;
}) => (
  <Card className={className}>
    {title && (
      <CardHeader className='pb-2'>
        <CardTitle className='text-base'>{title}</CardTitle>
      </CardHeader>
    )}
    <CardContent className='whitespace-pre-line text-justify text-sm leading-relaxed'>
      <OfferMessageText text={text} />
    </CardContent>
  </Card>
);
